import React, { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  className = '',
  ...props
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-pink-600 hover:bg-pink-700 text-white shadow-sm hover:shadow-md';
      case 'secondary':
        return 'bg-gray-200 hover:bg-gray-300 text-gray-800 shadow-sm hover:shadow-md';
      case 'outline':
        return 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 shadow-sm hover:shadow-md';
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow-md';
      default:
        return 'bg-pink-600 hover:bg-pink-700 text-white shadow-sm hover:shadow-md';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'text-xs px-3 py-2';
      case 'md':
        return 'text-sm px-4 py-2.5';
      case 'lg':
        return 'text-base px-6 py-3.5';
      default:
        return 'text-sm px-4 py-2.5';
    }
  };

  return (
    <button
      className={`
        ${getVariantClasses()}
        ${getSizeClasses()}
        ${fullWidth ? 'w-full' : ''}
        rounded-lg font-medium transition-all duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-50 focus:ring-offset-2
        flex items-center justify-center
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      {...props}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

export default Button;